

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import {
  Box,
  Container,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Divider,
  Button,
  Card,
  CardContent,
  Grid,
  Snackbar
} from '@mui/material';

import {
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Apartment as ApartmentIcon,
  Layers as LayersIcon,
  AttachFile as AttachFileIcon,
  Check as CheckIcon
} from '@mui/icons-material';

export default function AdminReview() {
  const navigate = useNavigate();
  const { assessmentId } = useParams();

  const [data, setData] = useState(null);
  const [adminAnswers, setAdminAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [successSnackbar, setSuccessSnackbar] = useState(false);

  // Answer choices
  const answerOptions = [
    { code: 'INCOMPLETE', label: 'Incomplete', desc: 'No evidence of the practice', color: 'error' },
    { code: 'DOCUMENTED', label: 'Documented', desc: 'Policy exists but implementation is partial', color: 'warning' },
    { code: 'OPERATIONAL', label: 'Operational', desc: 'Practice implemented & functioning', color: 'info' },
    { code: 'VERIFIED', label: 'Verified', desc: 'Verified via strong evidence or 3rd-party', color: 'success' }
  ];

  useEffect(() => {
    fetchDetails();
  }, []);

  const fetchDetails = async () => {
    try {
      const response = await fetch(
        `https://py.taih.ai/api/admin/reviews/${assessmentId}/details`
      );

      if (response.ok) {
        const result = await response.json();
        setData(result);

        const initial = {};
        result.items.forEach(q => {
          initial[q.question_id] = q.user_answer_code;
        });
        setAdminAnswers(initial);

      } else {
        throw new Error("Failed to fetch data");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to load review details");
    } finally {
      setLoading(false);
    }
  };

  const updateAdminAnswer = (questionId, code) => {
    setAdminAnswers(prev => ({
      ...prev,
      [questionId]: code
    }));
  };

  const submitReview = async () => {
    setSubmitting(true);
    setError('');

    const body = {
      answers: Object.entries(adminAnswers).map(([question_id, answer_option_code]) => ({
        question_id: parseInt(question_id),
        answer_option_code
      }))
    };

    try {
      const response = await fetch(
        `https://py.taih.ai/api/admin/reviews/${assessmentId}/score`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', accept: 'application/json' },
          body: JSON.stringify(body)
        }
      );

      if (response.ok) {
        setSuccessSnackbar(true);
        setTimeout(() => navigate('/admin/dashboard'), 1500);
      } else {
        const errData = await response.json().catch(() => ({}));
        setError(errData.detail || "Failed to submit review");
      }
    } catch (err) {
      console.error(err);
      setError("Network error submitting review");
    } finally {
      setSubmitting(false);
    }
  };

  const openEvidence = (evidence_id) => {
    window.open(
      `https://py.taih.ai/api/admin/files/${evidence_id}/download`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  if (loading) {
    return (
      <Box 
        sx={{ 
          minHeight: '100vh', 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          fontFamily: 'Helvetica, Arial, sans-serif'
        }}
      >
        <CircularProgress sx={{ color: '#004494' }} />
        <Typography 
          sx={{ 
            ml: 2,
            fontFamily: 'Helvetica, Arial, sans-serif'
          }}
        >
          Loading review...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert 
          severity="error"
          sx={{ fontFamily: 'Helvetica, Arial, sans-serif' }}
        >
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa', fontFamily: 'Helvetica, Arial, sans-serif' }}>

      {/* HEADER */}
      <Box sx={{ bgcolor: '#004494', color: 'white', py: 2 }}>
        <Container>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative' }}>
            <IconButton
              onClick={() => navigate('/admin/dashboard')}
              sx={{ 
                color: 'white', 
                position: 'absolute', 
                left: 0,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              <ArrowBackIcon />
            </IconButton>

            <Typography  
              variant="h5" 
              sx={{ 
                fontWeight: 600, 
                color: 'white',
                fontFamily: 'Helvetica, Arial, sans-serif'
              }}
            >
              Review Assessment #{assessmentId}
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container sx={{ py: 4 }}>

        {/* ================= ORGANIZATION PROFILE ================= */}
        <Paper elevation={3} sx={{ p: 4, mb: 4, borderRadius: 3 }}>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 300, 
              mb: 3,
              fontFamily: 'Helvetica, Arial, sans-serif',
              fontSize: '32px',
              color: '#000'
            }}
          >
            Organization Profile
          </Typography>

          <Grid container spacing={3} sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)' }}>
            {/* ORG NAME */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }} elevation={2}>
                <BusinessIcon sx={{ fontSize: 40, color: '#004494' }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      color: 'text.secondary',
                      fontFamily: 'Helvetica, Arial, sans-serif',
                      fontSize: '12px',
                      fontWeight: 400
                    }}
                  >
                    Organization Name
                  </Typography>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 400,
                      fontFamily: 'Helvetica, Arial, sans-serif',
                      fontSize: '16px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                    title={data.profile.org_name}
                  >
                    {data.profile.org_name}
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            {/* COMPANY TYPE */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }} elevation={2}>
                <ApartmentIcon sx={{ fontSize: 40, color: '#004494' }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      color: 'text.secondary',
                      fontFamily: 'Helvetica, Arial, sans-serif',
                      fontSize: '12px',
                      fontWeight: 400
                    }}
                  >
                    Company Type
                  </Typography>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 400,
                      fontFamily: 'Helvetica, Arial, sans-serif',
                      fontSize: '16px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                    title={data.profile.company_type}
                  >
                    {data.profile.company_type}
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            {/* AI STAGE */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }} elevation={2}>
                <LayersIcon sx={{ fontSize: 40, color: '#004494' }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      color: 'text.secondary',
                      fontFamily: 'Helvetica, Arial, sans-serif',
                      fontSize: '12px',
                      fontWeight: 400
                    }}
                  >
                    AI Stage
                  </Typography>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 400,
                      fontFamily: 'Helvetica, Arial, sans-serif',
                      fontSize: '16px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                    title={data.profile.stage}
                  >
                    {data.profile.stage}
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            {/* EMAIL */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }} elevation={2}>
                <EmailIcon sx={{ fontSize: 40, color: '#004494' }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      color: 'text.secondary',
                      fontFamily: 'Helvetica, Arial, sans-serif',
                      fontSize: '12px',
                      fontWeight: 400
                    }}
                  >
                    Contact Email
                  </Typography>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 400,
                      fontFamily: 'Helvetica, Arial, sans-serif',
                      fontSize: '16px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                    title={data.profile.contact_email || 'N/A'}
                  >
                    {data.profile.contact_email || 'N/A'}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Paper>

        {/* ================= RESPONSES + ADMIN OVERRIDE ================= */}
        <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 500, 
              mb: 3,
              fontFamily: 'Helvetica, Arial, sans-serif',
              fontSize: '24px',
              color: '#000'
            }}
          >
            Responses & Review
          </Typography>

          {data.items.map(item => (
            <Card
              key={item.question_id}
              sx={{ mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
            >
              <CardContent>

                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 500, 
                    mb: 1, 
                    color: '#004494',
                    fontFamily: 'Helvetica, Arial, sans-serif',
                    fontSize: '18px'
                  }}
                >
                  {item.principle}
                </Typography>

                <Typography 
                  sx={{ 
                    mb: 2, 
                    lineHeight: 1.6,
                    fontFamily: 'Helvetica, Arial, sans-serif',
                    fontSize: '16px',
                    fontWeight: 400
                  }}
                >
                  {item.question_text}
                </Typography>

                {/* ==== OPTIONS ==== */}
                <Box sx={{ mb: 3 }}>
                  {answerOptions.map(option => {
                    const isSelected = adminAnswers[item.question_id] === option.code;

                    return (
                      <Paper
                        key={option.code}
                        onClick={() => updateAdminAnswer(item.question_id, option.code)}
                        sx={{
                          p: 2,
                          mb: 1.5,
                          cursor: 'pointer',
                          borderRadius: 2,
                          border: isSelected
                            ? '2px solid #004494'
                            : '1px solid #ccc',
                          backgroundColor: isSelected ? 'rgba(0, 68, 148, 0.05)' : 'white',
                          position: 'relative',
                          '&:hover': { 
                            boxShadow: 2,
                            backgroundColor: isSelected ? 'rgba(0, 68, 148, 0.08)' : 'rgba(0, 68, 148, 0.02)'
                          }
                        }}
                        elevation={0}
                      >
                        {/* LEFT TICK FOR ADMIN SELECTED ANSWER */}
                        {isSelected && (
                          <CheckIcon
                            sx={{
                              position: 'absolute',
                              left: 8,
                              top: '50%',
                              transform: 'translateY(-50%)',
                              fontSize: 20,
                              color: '#4caf50'
                            }}
                          />
                        )}

                        <Typography 
                          variant="subtitle1" 
                          sx={{ 
                            fontWeight: 500, 
                            ml: isSelected ? 3 : 0,
                            fontFamily: 'Helvetica, Arial, sans-serif',
                            fontSize: '16px'
                          }}
                        >
                          {option.label}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: 'text.secondary', 
                            ml: isSelected ? 3 : 0,
                            fontFamily: 'Helvetica, Arial, sans-serif',
                            fontSize: '14px',
                            fontWeight: 400
                          }}
                        >
                          {option.desc}
                        </Typography>
                      </Paper>
                    );
                  })}
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* USER COMMENT */}
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    fontWeight: 500, 
                    mb: 1,
                    fontFamily: 'Helvetica, Arial, sans-serif',
                    fontSize: '14px'
                  }}
                >
                  User Comment
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{ p: 2, bgcolor: 'grey.50', borderColor: 'grey.300', mb: 2 }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Helvetica, Arial, sans-serif',
                      fontSize: '14px',
                      fontWeight: 400,
                      lineHeight: 1.5
                    }}
                  >
                    {item.user_comment}
                  </Typography>
                </Paper>

                {/* EVIDENCE */}
                {item.evidence.length > 0 && (
                  <>
                    <Typography 
                      variant="subtitle2" 
                      sx={{ 
                        fontWeight: 500, 
                        mb: 1,
                        fontFamily: 'Helvetica, Arial, sans-serif',
                        fontSize: '14px'
                      }}
                    >
                      Supporting Files
                    </Typography>

                    {item.evidence.map(ev => (
                      <Box
                        key={ev.evidence_id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 1,
                          cursor: 'pointer'
                        }}
                        onClick={() => openEvidence(ev.evidence_id)}
                      >
                        <AttachFileIcon sx={{ color: '#004494' }} />
                        <Typography 
                          sx={{ 
                            textDecoration: 'underline',
                            fontFamily: 'Helvetica, Arial, sans-serif',
                            fontSize: '14px',
                            fontWeight: 400,
                            color: '#004494'
                          }}
                        >
                          {ev.file_name}
                        </Typography>
                      </Box>
                    ))}
                  </>
                )}

              </CardContent>
            </Card>
          ))}

          {/* SUBMIT */}
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Button
              variant="contained"
              size="large"
              onClick={submitReview}
              disabled={submitting}
              sx={{ 
                minWidth: 240,
                backgroundColor: '#004494',
                color: 'white',
                fontFamily: 'Helvetica, Arial, sans-serif',
                fontSize: '16px',
                fontWeight: 400,
                '&:hover': {
                  backgroundColor: '#003366'
                },
                '&:disabled': {
                  backgroundColor: '#ccc',
                  color: 'rgba(0, 0, 0, 0.26)'
                }
              }}
            >
              {submitting ? 'Submitting Review...' : 'Submit Review'}
            </Button>
          </Box>
        </Paper>
      </Container>

      {/* SUCCESS SNACKBAR */}
      <Snackbar
        open={successSnackbar}
        onClose={() => setSuccessSnackbar(false)}
        autoHideDuration={1500}
        message="Review submitted successfully!"
        sx={{
          '& .MuiSnackbarContent-root': {
            fontFamily: 'Helvetica, Arial, sans-serif'
          }
        }}
      />
    </Box>
  );
}